import type { HexColorString, RGBA, RGBAColor, RGBColor } from '@cesdk/cesdk-js';
import type CreativeSDK from '@cesdk/cesdk-js';
import { GQLProductSideSideType } from '@theprintbar/graphql';
import { Pantone, getClosestPantone, getPantoneColours, hexToPixel } from '@theprintbar/shared';
import { getPage, isGraphicOrTextBlock, isImageBlock, isTextBlock } from './block';
import { getMetaColours } from './metadata';
import { getAllPageBlocks } from './page';

export const rgbColourToPixel = (colour: RGBColor) => [colour.r * 255, colour.g * 255, colour.b * 255].map(Math.round) as number[];
export const rgbToPixel = (colour: number[]) => colour.map(c => Math.round(c * 255)) as number[];

export async function getColours(pixels: Uint8ClampedArray, endAtCount = 10) {
	const colours: Pantone[] = [];
	const pantones = await getPantoneColours();

	for (let i = 0; i < pixels.length; i += 4) {
		if (endAtCount && colours.length >= endAtCount) break;

		const r = pixels[i];
		const g = pixels[i + 1];
		const b = pixels[i + 2];
		const a = pixels[i + 3];

		const pantone = getClosestPantone([r, g, b], pantones);
		if (a !== 255) continue;

		if (!colours.some(({ name }) => name === pantone.name)) {
			colours.push(pantone);
		}
	}
	return colours;
}

export function colourToHex(colour: RGBColor | HexColorString | number[]): string {
	if (typeof colour === 'string') {
		return colour;
	} else if (Array.isArray(colour)) {
		const [r, g, b] = colour;
		return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
	}

	const { r, g, b } = colour;
	//RGB is a range from 0 - 1 so we need to multiply by 255
	return `#${((1 << 24) + ((r * 255) << 16) + ((g * 255) << 8) + b * 255).toString(16).slice(1)}`;
}

export function hexToRGBAColor(hex: string): RGBAColor {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);

	//RGBA is a range from 0 - 1 so we need to divide by 255
	return { r: r / 255, g: g / 255, b: b / 255, a: 1 };
}

export function colourArrayToRGBA(colour: number[]): RGBAColor {
	const [r, g, b] = colour;
	return { r: r / 255, g: g / 255, b: b / 255, a: 1 };
}

export function getRecolourList(colours: Pantone[], replacements: Record<string, Pantone> = {}) {
	const recolourList: Pantone[] = [];

	for (const colour of colours) {
		let newColour = replacements[colour.name] || colour;
		if (!recolourList.some(({ name }) => name === newColour.name)) {
			recolourList.push(newColour);
		}
	}

	return recolourList;
}

export async function getPageColours(editor: CreativeSDK, side: GQLProductSideSideType) {
	const page = getPage(editor, side);
	return await getEditorColours(editor, page);
}

export async function getEditorColours(editor: CreativeSDK, page?: number) {
	if (!editor) return [];
	const { block: engineBlock } = editor.engine;
	const blocks = page ? engineBlock.getChildren(page) : getAllPageBlocks(editor);

	let colours: Pantone[] = [];
	const pantones = await getPantoneColours();

	for (const block of blocks) {
		const blockType = engineBlock.getType(block);

		if (!isGraphicOrTextBlock(blockType)) continue;

		if (isImageBlock(blockType, editor, block)) {
			const blockColours = await getMetaColours(editor, block);
			for (const colour of blockColours) {
				if (!colours.some(({ name }) => name === colour.name)) {
					colours.push(colour);
				}
			}
		} else {
			let blockColours: RGBA[] = [];

			try {
				blockColours.push(engineBlock.getFillSolidColor(block));
			} catch (e) {
				//We don't care if this fails
			}

			if (isTextBlock(blockType)) {
				blockColours = (engineBlock.getTextColors(block) as RGBAColor[]).map(({ r, g, b }) => [r, g, b, 1]);
			}

			if (!!blockColours.length) {
				blockColours.forEach(colour => {
					const pantone = getClosestPantone([colour[0] * 255, colour[1] * 255, colour[2] * 255], pantones);
					if (!colours.some(({ name }) => name === pantone.name)) {
						//Insert the colour at the front of the array
						colours = [pantone, ...colours];
					}
				});
			}
		}
	}

	return colours;
}

export function updateSVGColours(svgText: string, palette: Pantone[]) {
	const colorRegex = /#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})/;
	let svgArray = svgText.split(colorRegex);

	for (let i = 0; i < svgArray.length; i++) {
		if (i % 2 === 0) continue;
		const hex = `#${svgArray[i]}`;
		const pantone = getClosestPantone(hexToPixel(hex), palette);
		svgArray[i] = colourToHex(pantone);
	}

	return svgArray.join('');
}

export default {
	getColours,
	colourToHex,
	hexToRGBAColor,
	colourArrayToRGBA,
	getPageColours,
	getEditorColours,
};
